import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Gift, ArrowLeft, Loader2, Store, User } from 'lucide-react';
import { Footer } from '@/components/Footer';

export default function RegisterPage() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: '', email: '', password: '', role: '', business_name: '', birthday: '', phone: '', rgpd_consent: false, cgu_consent: false });
    const [loading, setLoading] = useState(false);

    const updateForm = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.role) { toast.error('Choisissez votre profil'); return; }
        if (form.role === 'merchant' && !form.business_name) { toast.error('Nom de commerce requis'); return; }
        if (form.role === 'client' && !form.rgpd_consent) { toast.info('Vous pourrez activer le consentement RGPD plus tard dans votre profil'); }
        if (!form.cgu_consent) { toast.error('Veuillez accepter les CGU et la politique de confidentialité'); return; }
        setLoading(true);
        try {
            const user = await register(form);
            toast.success(`Bienvenue sur FidelRun, ${user.name} !`);
            navigate(user.role === 'merchant' ? '/commercant' : '/client');
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50/50 to-white flex flex-col">
            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center gap-2 mb-6 text-sm text-slate-500 hover:text-emerald-600 transition-colors" data-testid="back-home-link">
                        <ArrowLeft className="w-4 h-4" /> Retour
                    </Link>
                    <div className="flex justify-center mb-4">
                        <div className="w-20 h-20 rounded-xl flex items-center justify-center overflow-hidden">
                            <img src="/logo-fr-small.png" alt="FidelRun" className="w-20 h-20 object-contain" />
                        </div>
                    </div>
                    <h1 className="font-heading text-2xl font-bold mb-1">Créer un compte</h1>
                    <p className="text-sm text-slate-500">Rejoignez FidelRun en quelques secondes</p>
                </div>

                <Card className="border-emerald-100/50 rounded-2xl shadow-lg shadow-emerald-900/5">
                    <CardContent className="p-6">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Role Selection */}
                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-slate-700">Vous êtes</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setForm(prev => ({ ...prev, role: 'merchant', cgu_consent: false }))}
                                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${form.role === 'merchant' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-emerald-200'}`}
                                        data-testid="role-merchant-btn"
                                    >
                                        <Store className={`w-6 h-6 ${form.role === 'merchant' ? 'text-emerald-600' : 'text-slate-400'}`} />
                                        <span className={`text-sm font-medium ${form.role === 'merchant' ? 'text-emerald-700' : 'text-slate-600'}`}>Commerçant</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setForm(prev => ({ ...prev, role: 'client', cgu_consent: false }))}
                                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${form.role === 'client' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-emerald-200'}`}
                                        data-testid="role-client-btn"
                                    >
                                        <User className={`w-6 h-6 ${form.role === 'client' ? 'text-emerald-600' : 'text-slate-400'}`} />
                                        <span className={`text-sm font-medium ${form.role === 'client' ? 'text-emerald-700' : 'text-slate-600'}`}>Client</span>
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="name" className="text-sm font-medium text-slate-700">Nom complet</Label>
                                <Input id="name" placeholder="Jean Dupont" value={form.name} onChange={e => updateForm('name', e.target.value)} required className="h-11 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/10" data-testid="register-name-input" />
                            </div>

                            {form.role === 'merchant' && (
                                <div className="space-y-2">
                                    <Label htmlFor="business" className="text-sm font-medium text-slate-700">Nom du commerce</Label>
                                    <Input id="business" placeholder="Mon Commerce" value={form.business_name} onChange={e => updateForm('business_name', e.target.value)} className="h-11 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/10" data-testid="register-business-input" />
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
                                <Input id="email" type="email" placeholder="votre@email.com" value={form.email} onChange={e => updateForm('email', e.target.value)} required className="h-11 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/10" data-testid="register-email-input" />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-sm font-medium text-slate-700">Mot de passe</Label>
                                <Input id="password" type="password" placeholder="Min. 6 caractères" value={form.password} onChange={e => updateForm('password', e.target.value)} required minLength={6} className="h-11 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/10" data-testid="register-password-input" />
                            </div>

                            {form.role === 'client' && (
                                <>
                                    <div className="space-y-2">
                                        <Label className="text-sm font-medium text-slate-700">Date de naissance</Label>
                                        <div className="grid grid-cols-3 gap-2">
                                            <select
                                                value={form.birthday ? parseInt(form.birthday.split('-')[2]) : ''}
                                                onChange={e => {
                                                    const parts = (form.birthday || '2000-01-01').split('-');
                                                    updateForm('birthday', `${parts[0]}-${parts[1]}-${String(e.target.value).padStart(2,'0')}`);
                                                }}
                                                className="h-11 px-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
                                                data-testid="register-birthday-day"
                                            >
                                                <option value="">Jour</option>
                                                {Array.from({length:31},(_,i)=>i+1).map(d => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                            <select
                                                value={form.birthday ? parseInt(form.birthday.split('-')[1]) : ''}
                                                onChange={e => {
                                                    const parts = (form.birthday || '2000-01-01').split('-');
                                                    updateForm('birthday', `${parts[0]}-${String(e.target.value).padStart(2,'0')}-${parts[2]}`);
                                                }}
                                                className="h-11 px-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
                                                data-testid="register-birthday-month"
                                            >
                                                <option value="">Mois</option>
                                                {['Jan','Fev','Mar','Avr','Mai','Juin','Juil','Aout','Sep','Oct','Nov','Dec'].map((m,i)=> <option key={i+1} value={i+1}>{m}</option>)}
                                            </select>
                                            <select
                                                value={form.birthday ? parseInt(form.birthday.split('-')[0]) : ''}
                                                onChange={e => {
                                                    const parts = (form.birthday || '2000-01-01').split('-');
                                                    updateForm('birthday', `${e.target.value}-${parts[1]}-${parts[2]}`);
                                                }}
                                                className="h-11 px-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
                                                data-testid="register-birthday-year"
                                            >
                                                <option value="">Année</option>
                                                {Array.from({length:80},(_,i)=> new Date().getFullYear() - 10 - i).map(y => <option key={y} value={y}>{y}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-sm font-medium text-slate-700">Téléphone</Label>
                                        <Input
                                            type="tel"
                                            placeholder="06 12 34 56 78"
                                            value={form.phone}
                                            onChange={e => updateForm('phone', e.target.value)}
                                            className="h-11 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/10"
                                            data-testid="register-phone-input"
                                        />
                                    </div>

                                    <label className="flex items-start gap-2.5 p-3 bg-slate-50 rounded-xl cursor-pointer" data-testid="register-rgpd-label">
                                        <input
                                            type="checkbox"
                                            checked={form.rgpd_consent}
                                            onChange={e => updateForm('rgpd_consent', e.target.checked)}
                                            className="mt-0.5 w-4 h-4 rounded border-slate-300 accent-emerald-500"
                                            data-testid="register-rgpd-checkbox"
                                        />
                                        <span className="text-[11px] text-slate-500 leading-relaxed">
                                            J'accepte que mes données personnelles soient utilisées dans le cadre du programme de fidélité, conformément au <span className="text-emerald-600 font-medium">RGPD</span>.
                                        </span>
                                    </label>
                                </>
                            )}

                            {/* CGU Consent - Both roles */}
                            {form.role && (
                                <label className="flex items-start gap-2.5 p-3 bg-slate-50 rounded-xl cursor-pointer" data-testid="register-cgu-label">
                                    <input
                                        type="checkbox"
                                        checked={form.cgu_consent}
                                        onChange={e => updateForm('cgu_consent', e.target.checked)}
                                        className="mt-0.5 w-4 h-4 rounded border-slate-300 accent-emerald-500"
                                        data-testid="register-cgu-checkbox"
                                    />
                                    <span className="text-[11px] text-slate-500 leading-relaxed">
                                        J'accepte les <Link to={form.role === 'merchant' ? '/cgu-professionnels' : '/cgu-clients'} target="_blank" className="text-emerald-600 font-medium hover:underline">Conditions Générales d'Utilisation</Link> et la <Link to="/confidentialite" target="_blank" className="text-emerald-600 font-medium hover:underline">Politique de Confidentialité</Link>.
                                    </span>
                                </label>
                            )}

                            <Button type="submit" disabled={loading || !form.cgu_consent} className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/20 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-testid="register-submit-btn">
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer mon compte"}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                <p className="text-center mt-6 text-sm text-slate-500">
                    Déjà un compte ?{' '}
                    <Link to="/connexion" className="text-emerald-600 font-medium hover:underline" data-testid="goto-login-link">Se connecter</Link>
                </p>
                </div>
            </div>
            <Footer />
        </div>
    );
}
